/* eslint-disable fp/no-mutation */
import React, { ReactElement, useState } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch } from 'src/store/hooks'
import {
  updateCAPCEAccreditation,
  fetchEventCompliances,
  fetchCAPCEAccreditation,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import { CAPCEAccreditationV2 } from 'src/v3/core/types'

type CAPCEFieldEditableProps = {
  eventID: string
  accreditation: CAPCEAccreditationV2
  fieldName: keyof Pick<
    CAPCEAccreditationV2,
    | 'courseCode'
    | 'expirationDate'
    | 'applicationDate'
    | 'submissionDate'
    | 'topicArea'
    | 'classType'
    | 'allClearDate'
    | 'goLiveDate'
  >
  fieldLabel: string
  fieldType: 'text' | 'date' | 'select'
  options?: string[] // For select fields
  required?: boolean
  viewMode?: 'inline' | 'modal' // inline for planning, modal for compliance
  onClose?: () => void // Called when modal is closed (modal mode only)
}

let topicAreas = [
  'Abdominal Emergencies',
  'Abuse',
  'ACLS',
  'Active Shooter',
  'Acute Coronary Syndromes',
  'Adult Respiratory Emergencies',
  'Advanced Airway',
  'AEMT Refresher',
  'Aeromedical Transport',
  'Allergy and Anaphylaxis',
  'ALS',
  'Altered Mental Status',
  'Ambulance Safety',
  'AMLS',
  'Anatomy and Physiology',
  'APLS',
  'Assessment',
  'Asthma',
  'At Risk Populations',
  'Autism',
  'Basic Airway',
  'Blood Borne Pathogens',
  'BLS',
  'Burns',
  'Capnography',
  'Cardiology',
  'CBRNE',
  'Cervical Injuries',
  'Chest Injuries',
  'CHF',
  'CNS Injury',
  'Communications',
  'Community Paramedicine',
  'Congenital Defects',
  'COPD',
  'Corona Virus',
  'COVID-19',
  'CPAP',
  'CPR',
  'Crew Resource Management',
  'Critical Care',
  'Critical Care Transport',
  'Critical Decision Making',
  'Culture of Safety',
  'Death',
  'Depression',
  'Diabetic Emergencies',
  'Disaster Management',
  'Diving and Hyperbarics',
  'Documentation',
  'Ebola',
  'Educator',
  'EKG Recognition',
  'Electrolyte Imbalance',
  'Emergency Child Birth',
  'Emergency Medical Dispatch (EMD)',
  'Emotional Resilience',
  'EMR Refresher Course',
  'EMS Self Defense',
  'EMT Refresher',
  'Endocrinology',
  'Envenomations',
  'Environmental Emergencies',
  'Ethics',
  'Evidence Based Guidelines',
  'Flight Physiology',
  'Fluid Resuscitation',
  'Fracture Management',
  'Geriatrics',
  'GI and GU Emergencies',
  'Gynecology',
  'Hazardous Materials',
  'Head Injuries',
  'Health and Wellness',
  'Heart Rhythms',
  'Hematology',
  'Hemorrhage Control',
  'Human Trafficking',
  'Hygiene Vaccinations',
  'Hypertension',
  'Immunology',
  'Infection Control',
  'Infectious Disease',
  'Intraosteous Infusion',
  'ITLS',
  'IV Therapy',
  'K9 Officer Care',
  'Leadership',
  'Legal',
  'LVAD',
  'Management Training',
  'Mass Violence',
  'Medical Director',
  'Medical Emergencies',
  'Medication Delivery',
  'MRSA',
  'Neonatology',
  'Neurology',
  'Nephrology',
  'Obesity',
  'Obstetrics',
  'Oncology',
  'Operations',
  'Opioids',
  'OSHA',
  'Oxygenation',
  'Pain Management',
  'PALS',
  'Paramedic Refresher',
  'Pediatric Assessment',
  'Pediatric Cardiac Arrest',
  'Pediatric Emergency Standards',
  'Pediatric Medical Emergencies',
  'Pediatric Respiratory Emergencies',
  'Pediatric Shock',
  'Pediatric Trauma',
  'Pediatrics',
  'Pharmacology',
  'Phlebotomy',
  'PHTLS',
  'Post Resuscitation Care',
  'Psychiatric Emergencies',
  'PTSD',
  'Quality Improvement',
  'Research',
  'Respiratory Emergencies',
  'Resuscitation',
  'Safety',
  'Seizures',
  'Sepsis',
  'Shock',
  'Skills',
  'Special Needs',
  'Special Topics',
  'Spinal Restriction',
  'Sports Injuries',
  'Stroke',
  'Tactical',
  'Terrorism Response',
  'Toxicology',
  'Traffic Incident Management',
  'Trauma',
  'Trauma Triage',
  'Traumatic Brain Injuries',
  'Triage',
  'Ultrasound',
  'Urology',
  'Ventilation',
  'Ventricular Assist Devices',
  'Wilderness Medicine',
]

let classTypes = [
  'ARV',
  'Cardiac',
  'Critical Care',
  'Education',
  'Medical',
  'OBGYN',
  'Operations',
  'Trauma',
  'Pediatrics',
  'Other',
]

export default ({
  eventID,
  accreditation,
  fieldName,
  fieldLabel,
  fieldType,
  options = [],
  required = false,
  viewMode = 'inline',
  onClose,
}: CAPCEFieldEditableProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [isEditing, setIsEditing] = useState(false)
  let [loading, setLoading] = useState(false)

  let currentValue = accreditation?.[fieldName] || ''

  // Format dates for display
  if (fieldType === 'date' && currentValue) {
    let date = new Date(currentValue as string)
    if (!Number.isNaN(date.getTime())) {
      ;[currentValue] = [date.toISOString().split('T')[0]]
    }
  }

  let form = useForm({
    defaultValues: {
      [fieldName]: currentValue,
    },
    onSubmit: async ({ value }) => {
      setLoading(true)
      try {
        let updateValue = value[fieldName]

        // Convert date strings to ISO format for API
        if (fieldType === 'date' && updateValue) {
          updateValue = new Date(updateValue as string).toISOString()
        }

        let fieldUpdates: any = {
          id: accreditation.id,
          [fieldName]: updateValue,
        }

        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(updateCAPCEAccreditation(fieldUpdates))
        // Refetch both accreditation and compliance statuses
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchCAPCEAccreditation(eventID))
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchEventCompliances())
        setIsEditing(false)

        // Close modal if in modal mode
        if (viewMode === 'modal' && onClose) {
          onClose()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update CAPCE accreditation field:', error)
      } finally {
        setLoading(false)
      }
    },
  })

  let renderFieldDisabled = () => {
    if (fieldType === 'date') {
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            sx={{ bgcolor: 'background.paperDark' }}
            value={currentValue ? dayjs(currentValue) : null}
            disabled
            slotProps={{
              textField: {
                fullWidth: true,
                slotProps: { htmlInput: { placeholder: 'Not set' } },
              },
              openPickerButton: {
                sx: { display: 'none' }, // Hide calendar button in disabled/display mode
              },
            }}
          />
        </LocalizationProvider>
      )
    }

    if (fieldType === 'select') {
      return (
        <FormControl fullWidth>
          <Select
            sx={{ bgcolor: 'background.paperDark' }}
            value={currentValue || ''}
            displayEmpty
            disabled
          >
            <MenuItem value={currentValue || ''}>
              {currentValue ? String(currentValue) : <em>Not set</em>}
            </MenuItem>
          </Select>
        </FormControl>
      )
    }

    // Default text field
    return (
      <TextField
        sx={{ bgcolor: 'background.paperDark' }}
        fullWidth
        value={currentValue || ''}
        placeholder="Not set"
        disabled
        aria-label={fieldLabel}
      />
    )
  }

  let renderField = () => {
    if (fieldType === 'date') {
      return (
        <form.Field
          name={fieldName}
          children={(field) => (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{ bgcolor: 'background.paperDark' }}
                value={field.state.value ? dayjs(field.state.value) : null}
                onChange={(val) => {
                  if (!val) {
                    field.handleChange('')
                  } else {
                    field.handleChange(val.format())
                  }
                }}
                onClose={field.handleBlur}
                disabled={loading}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    onBlur: field.handleBlur,
                  },
                }}
              />
            </LocalizationProvider>
          )}
        />
      )
    }

    if (fieldType === 'select') {
      let selectOptions =
        fieldName === 'topicArea'
          ? topicAreas
          : fieldName === 'classType'
            ? classTypes
            : options
      return (
        <form.Field
          name={fieldName}
          children={(field) => (
            <FormControl fullWidth>
              <Select
                sx={{ bgcolor: 'background.paperDark' }}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                disabled={loading}
              >
                {selectOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      )
    }

    // Default text field
    return (
      <form.Field
        name={fieldName}
        children={(field) => (
          <TextField
            sx={{ bgcolor: 'background.paperDark' }}
            fullWidth
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            disabled={loading}
            aria-label={fieldLabel}
          />
        )}
      />
    )
  }

  let renderContent = () => (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          minHeight: '24px',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'medium',
          }}
        >
          {fieldLabel}
          {required && (
            <Typography color="error" component="span" aria-label="required">
              {' '}
              *
            </Typography>
          )}
        </Typography>
      </Box>

      {!isEditing ? (
        <Box sx={{ position: 'relative' }}>
          {renderFieldDisabled()}
          <Button
            variant="text"
            size="small"
            onClick={() => {
              form.setFieldValue(fieldName, currentValue)
              setIsEditing(true)
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              minWidth: 'auto',
              px: 1,
            }}
            aria-label={`Edit ${fieldLabel}`}
          >
            Edit
          </Button>
        </Box>
      ) : (
        <Box>
          <Box sx={{ mb: 2 }}>
            {fieldType === 'select' && (
              <InputLabel sx={{ mb: 1, fontSize: '0.875rem' }}>
                {fieldLabel}
              </InputLabel>
            )}
            {renderField()}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => form.handleSubmit()}
              disabled={loading}
              aria-label={`Save changes to ${fieldLabel}`}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setIsEditing(false)
                form.reset()
                // Close modal if in modal mode
                if (viewMode === 'modal' && onClose) {
                  onClose()
                }
              }}
              disabled={loading}
              aria-label={`Cancel editing ${fieldLabel}`}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )

  // If modal mode, render the content directly (parent will wrap in modal)
  if (viewMode === 'modal') {
    return renderContent()
  }

  // Inline mode - render directly
  return renderContent()
}
